import React, { FC, useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  TooltipItem,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import HeaderBtn from '../../headerBtn/headerBtn';

import { ReactComponent as SettingIcon } from '../../../assets/icons/settings.svg';
import { ReactComponent as IncreaseIcon } from '../../../assets/icons/increase.svg';
import { ReactComponent as ReduceIcon } from '../../../assets/icons/reduce.svg';
import { ReactComponent as ArrowIcon } from '../../../assets/icons/chevron.svg';
import { ReactComponent as FiltersIcon } from '../../../assets/icons/filter.svg';
import { ReactComponent as ActiveFiltersIcon } from '../../../assets/icons/filter_active.svg';

import { AppDispatch } from '../../../redux/reducer/store';

import { actionAnalyticSkuAverage } from '../../../redux/action/charts/actionCharts';
import {
  reducerChangeDataCharts,
  reducerEditTypeChart,
} from '../../../redux/reducer/charts/reducer/reducerCharts';

import ChartComponent from '../../custom/chartComponet/chartComponent';
import ChartLoader from '../../custom/chartLoader/chartLoader';
import ScrollingLegend from '../../custom/legend/legend';

import {
  createFormatDateFunction,
  filteredZeroData,
  reverseChartsList,
  lineChartList,
  monthOptions,
  defaultFilter,
  normList,
  tickData,
  getColorOptions,
} from '../../../utils/chartsData';
import { formatDateForMapReport } from '../../../utils/formatDate';

import {
  IChartOptions,
  IFiltersCharts,
  IContextOptions,
} from '../../../types/chartsInterface';

import '../charts.scss';

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ChartDataLabels,
  PointElement,
  LineElement,
);

const typesCharts = [
  {
    value: 'lineBar',
    label: 'Линейчатая',
  },
  {
    value: 'lineBarStacked',
    label: 'Линейчатая с накоплением',
  },
  {
    value: 'lineBarNorm',
    label: 'Нормированная линейчатая с накоплением',
  },
  {
    value: 'bar',
    label: 'Гистограмма',
  },
  {
    value: 'barStacked',
    label: 'Гистограмма с накоплением',
  },
  {
    value: 'barStackedNorm',
    label: 'Нормированная гистограмма с накоплением',
  },
  {
    value: 'lineChart',
    label: 'График',
  },
  { value: 'lineChartStacked', label: 'График с накоплением' },
  {
    value: 'lineChartStackedNorm',
    label: 'Нормированный график с накоплением',
  },
];

const MediumFacingChart: FC<IChartOptions> = ({
  id,
  copyChart,
  isCopy,
  pageId,
}) => {
  const dispatch: any = useDispatch<AppDispatch>();
  const listRef = useRef<HTMLDivElement | null>(null);

  const { pending, desktopData, settingCharts, desktopInputValues } =
    useSelector((state: any) => state.charts?.chartsList);

  const colorList = getColorOptions(settingCharts?.mediumFace);
  const index =
    desktopData[pageId || 0]?.find((el: { id: number }) => el.id === id) || {};
  const skuAverageFilters =
    desktopInputValues?.[Number(pageId) || 0]?.[id] || null;

  const {
    type,
    labels,
    groupped,
    periodList,
    dataPrecent,
    dataSkuCharts,
    periodListOfDate,
  } = index;

  const {
    filters,
    areasOptions,
    chainsOptions,
    regionsOptions,
    projectOptions,
    contractsOptions,
  } = useSelector((state: any) => state.userReports.userReports);
  const { darkTheme } = useSelector((state: any) => state.theme.theme);

  const ticks = {
    ...tickData,
    color: darkTheme.isDarkTheme ? 'white' : 'black',
  };

  const [isIncreasedSize, setIsIncreasedSize] = useState<boolean>(false);
  const [isOpenSetting, setIsOpenSetting] = useState<boolean>(false);
  const [isOpenSectionCharts, setIsOpenSectionCharts] =
    useState<boolean>(false);
  const [isListRightPosition, setIsListRightPosition] =
    useState<boolean>(false);
  const [isListRightFiltersPosition, setIsListRightFiltersPosition] =
    useState<boolean>(false);
  const [isChangeData, setIsChangeData] = useState<boolean>();
  const [isloader, setIsloader] = useState<boolean>(true);
  const [isHiddenLabel, setIsHiddenLabel] = useState<string[]>([]);
  const [isVisiblelabel, setIsVisiblelabel] = useState<any[]>([]);
  const [isLabels, setIsLabels] = useState<string[]>([]);
  const [isPeriod, setIsPeriod] = useState<string[]>([]);
  const [isData, setIsData] = useState([]);

  const defaultStack: string[] = ['bar', 'lineBar', 'lineChart'];
  const grouppedName =
    groupped === 'subgroup'
      ? 'подгруппам'
      : groupped === 'group'
      ? 'группам'
      : groupped === 'category'
      ? 'подкатегориям'
      : 'категориям';

  const axisX = [
    {
      formatDate: (data: string) => {
        return formatDateForMapReport(data);
      },
    },
    {
      formatDate: createFormatDateFunction(contractsOptions),
    },
    {
      formatDate: createFormatDateFunction(projectOptions),
    },
    {
      formatDate: createFormatDateFunction(chainsOptions),
    },
    {
      formatDate: createFormatDateFunction(areasOptions),
    },
    {
      formatDate: createFormatDateFunction(regionsOptions),
    },
    {
      formatDate: createFormatDateFunction(monthOptions),
    },
  ];

  const data = {
    labels: periodList ? isPeriod : [],
    datasets: isData,
  };

  const optionsForBar = {
    responsive: true,
    indexAxis: type === 'lineBar' ? ('y' as const) : ('x' as const),
    layout: {
      padding: {
        top: type === 'lineBar' ? 0 : 30,
      },
    },
    scales: {
      x: {
        stacked:
          !defaultStack.includes(type) ||
          skuAverageFilters?.group?.value === '12',
        beginAtZero: true,
        ticks: ticks,
        grid: {
          color: darkTheme.isDarkTheme ? 'white' : 'black',
        },
      },
      y: {
        stacked:
          !defaultStack.includes(type) ||
          skuAverageFilters?.group?.value === '12',
        ticks: ticks,
        grid: {
          color: darkTheme.isDarkTheme ? 'white' : 'black',
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        position: 'nearest' as 'nearest',
        callbacks: {
          title: function (tooltipItem: TooltipItem<typeof type>[]) {
            return `${tooltipItem[0].label}`;
          },
          label: function (tooltipItem: TooltipItem<typeof type>) {
            return `${tooltipItem.dataset.label}: ${tooltipItem.formattedValue}`;
          },
        },
      },
      datalabels: {
        color: '#000',
        align: 'center' as const,
        anchor: 'center' as const,
        formatter: (value: number, context: IContextOptions) => {
          if (value > 0 && !lineChartList.includes(type)) {
            const { chart } = context;
            const fixedValue = value.toLocaleString('ru-RU');
            const barWidth = chart.getDatasetMeta(context.datasetIndex).data[
              context.dataIndex
            ].$animations.width._to;
            const barHeigth = chart.getDatasetMeta(context.datasetIndex).data[
              context.dataIndex
            ].$animations.height._to;
            const widthSize = fixedValue.toString().length * 10;
            if (barHeigth > 13 && barWidth >= widthSize) {
              return fixedValue;
            } else {
              return '';
            }
          } else {
            return '';
          }
        },
      },
    },
  };

  const optionsForStacked = {
    responsive: true,
    indexAxis: type === 'lineBarStacked' ? ('y' as const) : ('x' as const),
    layout: {
      padding: {
        top: 30,
      },
    },
    scales: {
      x: {
        stacked:
          !defaultStack.includes(type) ||
          skuAverageFilters?.group?.value === '12',
        ticks: ticks,
        grid: {
          color: darkTheme.color,
        },
      },
      y: {
        stacked:
          !defaultStack.includes(type) ||
          skuAverageFilters?.group?.value === '12',
        ticks: ticks,
        grid: {
          color: darkTheme.color,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        position: 'nearest' as 'nearest',
        callbacks: {
          title: function (tooltipItem: TooltipItem<typeof type>[]) {
            return `${tooltipItem[0].label}`;
          },
          label: function (tooltipItem: TooltipItem<typeof type>) {
            return `${tooltipItem.dataset.label}: ${tooltipItem.formattedValue}`;
          },
        },
      },
      datalabels: {
        color: '#000',
        align: 'center' as const,
        anchor: 'center' as const,
        formatter: (value: number, context: IContextOptions) => {
          if (value > 0 && !lineChartList.includes(type)) {
            const { chart } = context;
            const fixedValue = value.toLocaleString('ru-RU');
            const barWidth = chart.getDatasetMeta(context.datasetIndex).data[
              context.dataIndex
            ]?.$animations.width._to;
            const barHeigth = chart.getDatasetMeta(context.datasetIndex).data[
              context.dataIndex
            ].$animations.height._to;
            const widthSize = fixedValue.toString().length * 10;
            if (barHeigth > 13 && barWidth >= widthSize) {
              return fixedValue;
            } else {
              return '';
            }
          } else {
            return '';
          }
        },
      },
    },
  };

  const optionsForNormalise = {
    responsive: true,
    indexAxis: type === 'lineBarNorm' ? ('y' as const) : ('x' as const),
    layout: {
      padding: {
        top: 30,
      },
    },
    scales: {
      x: {
        max: type === 'lineBarNorm' ? 100 : undefined,
        stacked: !defaultStack.includes(type),
        ticks: ticks,
        grid: {
          color: darkTheme.color,
        },
      },
      y: {
        stacked: !defaultStack.includes(type),
        max: type === 'barStackedNorm' ? 100 : undefined,
        ticks: ticks,
        grid: {
          color: darkTheme.color,
        },
      },
    },
    plugins: {
      tooltip: {
        position: 'nearest' as 'nearest',
        callbacks: {
          title: function (tooltipItem: TooltipItem<typeof type>[]) {
            return `${tooltipItem[0].label}`;
          },
          label: function (tooltipItem: TooltipItem<typeof type>) {
            return `${tooltipItem.dataset.label}: ${tooltipItem.formattedValue}`;
          },
        },
      },
      legend: {
        display: false,
      },
      datalabels: {
        color: '#000',
        align: 'center' as const,
        anchor: 'center' as const,
        formatter: (value: number, context: IContextOptions) => {
          if (value > 0 && !lineChartList.includes(type)) {
            const { chart } = context;
            const fixedValue = value.toFixed();
            const barWidth = chart.getDatasetMeta(context.datasetIndex).data[
              context.dataIndex
            ].$animations.width._to;
            const barHeigth = chart.getDatasetMeta(context.datasetIndex).data[
              context.dataIndex
            ].$animations.height._to;
            const widthSize = fixedValue.length * 10;
            if (barHeigth > 13 && barWidth >= widthSize) {
              return value >= 3 ? `${fixedValue}%` : '';
            } else {
              return '';
            }
          } else {
            return '';
          }
        },
      },
    },
  };

  const actualOptionsChart = normList.includes(type)
    ? optionsForNormalise
    : defaultStack.includes(type)
    ? optionsForBar
    : optionsForStacked;

  const getChangeData = (data: IFiltersCharts) => {
    const changes: IFiltersCharts = {};

    for (const key in defaultFilter) {
      if (defaultFilter.hasOwnProperty(key)) {
        // @ts-ignore
        if (JSON.stringify(defaultFilter[key]) !== JSON.stringify(data[key])) {
          // @ts-ignore
          changes[key] = data[key];
        }
      }
    }

    for (const key in data) {
      if (data.hasOwnProperty(key) && !defaultFilter.hasOwnProperty(key)) {
        // @ts-ignore
        changes[key] = data[key];
      }
    }
    setIsChangeData(Object.keys(changes).length > 0);
    dispatch(
      reducerChangeDataCharts({
        hasChanged: Object.keys(changes).length > 0,
        changes,
      }),
    );
  };

  const handleClickSetting = () => {
    if (isOpenSetting) {
      setIsOpenSectionCharts(false);
    } else {
      getChangeData(skuAverageFilters);
    }
    setIsOpenSetting(!isOpenSetting);
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (listRef.current && !listRef.current.contains(e.target as Node)) {
      setIsOpenSetting(false);
      setIsOpenSectionCharts(false);
    }
  };

  const handleClickOnLegend = (name: string) => {
    if (!isHiddenLabel.includes(name)) {
      setIsHiddenLabel((prevItems: string[]) => {
        return [...prevItems, name];
      });
    } else {
      setIsHiddenLabel((prevItems: string[]) => {
        return prevItems.filter((item) => item !== name);
      });
    }
  };

  const handleClickFilterOnType = (name: string, index: number) => {
    dispatch(reducerEditTypeChart({ id, pageId, data: name }));
    setIsHiddenLabel([]);
  };

  useEffect(() => {
    if (skuAverageFilters) {
      getChangeData(skuAverageFilters);
    }
    // eslint-disable-next-line
  }, [skuAverageFilters]);

  useEffect(() => {
    if (isPeriod.length > 0) {
      const timeoutId = setTimeout(() => {
        const updatedData = dataSkuCharts
          ? normList.includes(type)
            ? dataPrecent
            : dataSkuCharts
          : [];
        const directionData = reverseChartsList.includes(type)
          ? updatedData.slice().reverse()
          : updatedData;
        setIsData(directionData);
        setIsVisiblelabel(filteredZeroData(updatedData));
      }, 500);

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line
  }, [isPeriod, normList, type]);

  useEffect(() => {
    setIsLabels(labels);
    // eslint-disable-next-line
  }, [labels]);

  useEffect(() => {
    if (skuAverageFilters?.group?.value === '0') {
      const period = periodListOfDate.map((el: string) => {
        return axisX[skuAverageFilters?.group?.value].formatDate(el);
      });
      setIsPeriod(period);
    } else {
      const period =
        periodList && skuAverageFilters
          ? periodList.map((el: string, index: number) => {
              if (skuAverageFilters?.group?.value === '7') {
                const parts = el.split('-')[1];
                return parts;
              } else if (skuAverageFilters?.group?.value === '8') {
                return axisX[6].formatDate(el);
              } else if (
                ['9', '10', '11', '12', '13', '14'].includes(
                  skuAverageFilters?.group?.value,
                )
              ) {
                return el;
              } else {
                return skuAverageFilters?.group?.value > 5
                  ? axisX[0].formatDate(el)
                  : axisX[skuAverageFilters?.group?.value].formatDate(el);
              }
            })
          : [];
      setIsPeriod(period);
    }
    // eslint-disable-next-line
  }, [periodList]);

  useEffect(() => {
    if (isOpenSetting) {
      const listEl = listRef.current;
      if (listEl) {
        const list = listEl.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        setIsListRightPosition(list.right + 168 > viewportWidth);
        setIsListRightFiltersPosition(
          list.right + 470 > viewportWidth || viewportWidth < 1030,
        );
      }
    }
    // eslint-disable-next-line
  }, [isOpenSetting]);

  useEffect(() => {
    if (filters) {
      setIsloader(true);
      dispatch(actionAnalyticSkuAverage(id, pageId)).then(() => {
        setIsloader(false);
      });
    }
    // eslint-disable-next-line
  }, [filters]);

  useEffect(() => {
    if (pending[pageId || 0]) {
      setIsloader(pending[pageId]?.includes(id));
    }
    // eslint-disable-next-line
  }, [pending]);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
    // eslint-disable-next-line
  }, []);

  return (
    <div
      className={`content-charts content-charts_long ${
        isIncreasedSize ? 'content-charts_active' : ''
      }`}
    >
      <div className='charts__header'>
        <p className='charts__title'>{`Средний фейсинг ${
          isCopy ? '(дубль)' : ''
        }`}</p>
        <div className='charts__btn'>
          <div>
            {!isIncreasedSize && (
              <IncreaseIcon
                className='charts__icon'
                onClick={() => setIsIncreasedSize(true)}
              />
            )}
            {isIncreasedSize && (
              <ReduceIcon
                className='charts__icon'
                onClick={() => setIsIncreasedSize(false)}
              />
            )}
          </div>
          <div className='charts__headerbtn'>
            <HeaderBtn
              content=' '
              purpose='filtersCharts'
              modalTitle='Настройка отображения данных'
              selectId={id}
              action={actionAnalyticSkuAverage}
              inputValuesCharts={skuAverageFilters}
            />
            {(!isChangeData || !skuAverageFilters) && (
              <FiltersIcon
                className={`charts__icon charts__icon_filters`}
                onClick={handleClickSetting}
              />
            )}
            {isChangeData && skuAverageFilters && (
              <ActiveFiltersIcon
                className={`charts__icon charts__icon_filters`}
                onClick={handleClickSetting}
              />
            )}
          </div>
          <div ref={listRef}>
            <SettingIcon
              className='charts__icon'
              onClick={handleClickSetting}
            />
            {isOpenSetting && (
              <ul
                className={`charts__setting ${
                  isListRightPosition ? 'charts__setting_right' : ''
                }`}
              >
                <li
                  onClick={() => {
                    handleClickSetting();
                    copyChart(id);
                  }}
                >
                  Дублировать
                </li>
                <li>
                  <HeaderBtn
                    content='Удалить'
                    purpose='removeCharts'
                    modalTitle='Удалить диаграмму?'
                    selectId={id}
                  />
                </li>
                <li
                  style={{ position: 'relative' }}
                  onClick={() => {
                    setIsOpenSectionCharts(!isOpenSectionCharts);
                  }}
                >
                  Тип диаграммы <ArrowIcon className='charts__icon' />
                  {isOpenSectionCharts && (
                    <ul
                      className={`charts__setting charts__setting_list ${
                        isListRightFiltersPosition
                          ? 'charts__setting_list_typeRight'
                          : ''
                      }`}
                    >
                      {typesCharts.map(
                        (
                          el: { value: string; label: string },
                          index: number,
                        ) => (
                          <li
                            key={index}
                            className={type === el.value ? 'item_active' : ''}
                            onClick={() =>
                              handleClickFilterOnType(el.value, index)
                            }
                          >
                            {el.label}
                          </li>
                        ),
                      )}
                    </ul>
                  )}
                </li>
                <li>
                  <HeaderBtn
                    content='Скриншот'
                    purpose='screenCharts'
                    modalTitle='Сделать скриншот'
                    dataCharts={{
                      data,
                      name: `Средний фейсинг ${isCopy ? '(дубль)' : ''}`,
                      type: type,
                      options: actualOptionsChart,
                      isIncreasedSize,
                      labels,
                      isHiddenItem: isHiddenLabel,
                      colorOptions: colorList,
                      chartType: 'mediumFace',
                    }}
                  />
                </li>
                <li>
                  <HeaderBtn
                    content='Настройка цвета'
                    purpose='changeColor'
                    modalTitle='Настройка цвета'
                    dataCharts={{ type: 'mediumFace', labels, isVisiblelabel }}
                  />
                </li>
                {/* <li onClick={handleClickSetting}>Выгрузить данные</li> */}
              </ul>
            )}
          </div>
        </div>
      </div>
      {isloader ? (
        <ChartLoader />
      ) : dataSkuCharts?.length === 0 ? (
        <p className='charts__text'>
          Данные по фейсам в выбранных вами отчётах не найдены. Убедитесь, что
          вы заказали эту услугу, расширьте рассматриваемый период или
          географический охват.
        </p>
      ) : (
        <>
          <ChartComponent
            type={type}
            data={data}
            isHiddenItem={isHiddenLabel}
            options={actualOptionsChart}
            isIncreasedSize={isIncreasedSize}
            loader={isloader}
            chartType={'mediumFace'}
            colorOptions={colorList}
          />
          {groupped !== 'skuName' && (
            <p className='charts__notice'>{`Вы выбрали очень много позиций для вывода на одной диаграмме, поэтому данные были сгруппированы по 
            ${grouppedName}. Если вы хотите получить данные по отдельным SKU, выберите их в фильтрах.`}</p>
          )}
          <ScrollingLegend
            colorOptions={colorList}
            isIncreasedSize={isIncreasedSize}
            isLabels={isLabels}
            isVisiblelabel={isVisiblelabel}
            handleClickOnLegend={handleClickOnLegend}
            isHiddenLabel={isHiddenLabel}
            type={type}
          />
        </>
      )}
    </div>
  );
};

export default MediumFacingChart;
